import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  footer?: React.ReactNode;
}
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  footer
}: ModalProps) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full m-4 h-[calc(100vh-2rem)]'
  };
  return createPortal(<div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0" data-id="element-200">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-201" />

      <div className={`
        relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full 
        transform transition-all animate-in fade-in zoom-in-95 duration-200
        flex flex-col max-h-[90vh]
        ${sizes[size]}
      `} data-id="element-202">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700" data-id="element-203">
          <h3 className="text-lg font-bold text-white" data-id="element-204">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800" data-id="element-205">
            <X className="h-5 w-5" data-id="element-206" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1" data-id="element-207">
          {children}
        </div>

        {/* Footer */}
        {footer && <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700 bg-slate-900/50 rounded-b-xl" data-id="element-208">
            {footer}
          </div>}
      </div>
    </div>, document.body);
}